import * as React from "react";
import { Button, Stack, InputLabel, TextField, InputBase } from "@mui/material";

import { useBreakpoint } from "../../hooks/useBreakpoint";
import { useXoraiaValue } from "../../hooks/useXoraia";
import { TUseXElement, useXElement } from "../../hooks/useXElement";
import { USE_SELECTED_INDEX } from "../../hooks/useSelectedElement";
import { useSetRecoilState } from "recoil";
import { getValueOnBreakpoint } from "../../lib/getValueOnBreakpoint";
import { clickHandler } from "../../lib/clickHandler";
import { XElementConfig } from "../../utils/interfaces/Elements/XElement.interfaces";

import { isEqual } from "lodash";

import { generateSide } from "../../lib/generateSide";
import {
  IXFormProps,
  TXFormMode,
  XFormProps,
} from "../../utils/interfaces/Elements/XForm.interfaces";
import { IFormInput } from "../../utils/interfaces/Elements/XForm.interfaces";
import { generateBorder } from "../../lib/generateBorder";
import { generateRadius } from "../../lib/generateRadius";

const compareProps = (prevProps: any, nextProps: any) => {
  return isEqual(prevProps, nextProps);
};

interface IXFormItem {
  required?: boolean;
  type: "email" | "text" | "textarea" | "phone" | "password" | "checkbox";
  placeholder?: string;
  label: string;
  name:
    | "fullname"
    | "email"
    | "message"
    | "address"
    | "phone"
    | "password"
    | "confirm"
    | "terms";
}

type TXFormGroup = { [key in TXFormMode]?: IXFormItem[] };

const X_FORM_GROUP: TXFormGroup = {
  "contact-us": [
    {
      type: "text",
      label: "Nom Complet",
      placeholder: "Entrez Votre Nom Complet",
      name: "fullname",
      required: true,
    },
    {
      type: "email",
      label: "Addresse Email",
      placeholder: "Entrez Votre Addresse Email",
      name: "email",
      required: true,
    },
    {
      type: "textarea",
      label: "Message",
      placeholder: "Entrez Votre Message",
      name: "message",
      required: true,
    },
  ],
  newsletter: [
    {
      type: "email",
      label: "Addresse Email",
      placeholder: "Entrez Votre Addresse Email",
      name: "email",
      required: true,
    },
  ],
};

const XForm: React.FC<XFormProps> = React.memo(
  ({ config = {} as XElementConfig }) => {
    // WE TRACK THE BREAK POINTS CHANGES
    const breakpoint = useBreakpoint();

    const { env } = useXoraiaValue();

    // WE ARE GETTING THE CURRENT ELEMENT FROM THE ATOM FAMILY
    const { xElement } = useXElement<TUseXElement<IXFormProps>>(config.index);

    // GARBING THE INDEX UPDATER FOR GENERAL USAGE
    const setSelectedElement = useSetRecoilState(USE_SELECTED_INDEX);

    // IF WE DONT HAVE THIS ELEMENT CONFIGS ADDED WE DON'T SHOW IT
    if (!xElement || Object.keys(xElement).length === 0) return null;

    // WE GET ALL DEVICES PROPS AS VALUE
    const { value } = xElement;

    // WE NOW USE THE CURRENT BREAKPOINT VALUE ON THIS ELEMENT
    const valueAtBreakPoint = getValueOnBreakpoint<IXFormProps>(
      value as any,
      breakpoint
    );

    const devProps = {
      id: config.index,
      onClick: (e: any) => clickHandler(e, setSelectedElement, config.index),
    };

    const {
      type = "contact-us",

      // buttonProps = {},
      // separator = '/',
      // showHomeIcons = false,
      // showHomeLabel = true,
      // textProps = {},
      width,
      height,
      padding,
      margin,
      border,
      borderRadius,
      bgColor,
      gap = "15px",
      btnText = "SOUMETTRE",
      btnHeight,
      btnMargin,
      btnBgColor,
      btnBorder,
      btnRadius,
      btnHoverBgColor,
      btnHoverBorder,
      btnHoverTextColor,
      btnTextColor,
      btnTextFontSize,
      btnTextFontWeight,
      btnWidth,
      ...inputProps
      // TODO IMPLEMENT BORDER RADIUS
    } = valueAtBreakPoint;

    if (!X_FORM_GROUP[type]) {
      return null;
    }

    const group = X_FORM_GROUP[type] || [];

    return (
      <Stack
        {...(env === "dev" && devProps)}
        component="form"
        spacing={gap}
        sx={{
          width: width || "100%",
          padding: generateSide("padding", padding),
          margin: generateSide("margin", margin),
          border: generateBorder(border),
          borderRadius: generateRadius(borderRadius),
          bgcolor: bgColor,
          height,
        }}
      >
        {group.map((item) => (
          <FormInput key={item.name} {...item} {...inputProps} />
        ))}
        <Button
          variant="contained"
          disableElevation
          color="primary"
          type="submit"
          sx={{
            display: "inline-block",
            bgcolor: btnBgColor,
            border: generateBorder(btnBorder),
            borderRadius: generateRadius(btnRadius),
            color: btnTextColor,
            fontSize: btnTextFontSize,
            fontWeight: btnTextFontWeight,
            width: btnWidth,
            height: btnHeight,
            margin: generateSide("margin", btnMargin),

            "&:hover": {
              bgcolor: btnHoverBgColor,
              border: generateBorder(btnHoverBorder),
              color: btnHoverTextColor,
            },
          }}
        >
          {btnText}
        </Button>
      </Stack>
    );
  },
  compareProps
);

// const generateFormItem = (item: IXFormItem) => {
//   if (!item) return null;

//   let element: React.ReactNode;

//   switch (item.type) {
//     case "text":
//     case "email":
//     case "password":
//       element = (
//         <FormInput
//           key={item.name}
//           name={item.name}
//           label={item.label}
//           placeholder={
//             item.placeholder || item.label + (item.required ? "*" : "")
//           }
//           // type={item.type}
//           // fullWidth
//           required={item.required}
//           // sx={{ mb: 2 }}
//         />
//       );
//       break;
//     case "textarea":
//       element = (
//         <TextField
//           key={item.name}
//           name={item.name}
//           label={item.label}
//           placeholder={
//             item.placeholder || item.label + (item.required ? "*" : "")
//           }
//           type={item.type}
//           required={item.required}
//           sx={{ mb: 2 }}
//           fullWidth
//           multiline
//           maxRows={7}
//           minRows={5}
//         />
//       );
//       break;
//     default:
//       return null;
//   }

//   return element;
// };

export default XForm;

interface FormInputProps extends IFormInput {
  name: string;
  label?: string;
  placeholder?: string;
  required?: boolean;
  type?: "email" | "text" | "textarea" | "phone" | "password" | "checkbox";
}

const FormInput: React.FC<FormInputProps> = ({
  name,
  label,
  placeholder,
  required,
  shrinkLabel,
  inputDesign = "material-standard",
  type = "text",
  inputBgColor = "#FFFFFF",
  inputBorder,
  inputRadius,
  inputFocusBgColor,
  inputFocusBorder,
  inputTextColor = "#000000",
  inputTextFontSize,
  inputTextFontWeight,
  inputHeight = "40px",
  labelColor = "#000000",
  labelFontSize,
  labelFontWeight,
  labelWidth,
  labelTextAlign = "left",
  labelVerticalAlign = "flex-start",
}) => {
  const id = "FF_" + name;
  const isMat = inputDesign.includes("material");
  const variant = isMat ? inputDesign.replace("material-", "") : "standard";

  const defaultBorder = !isMat
    ? inputBorder ?? { color: "#000000", style: "solid", width: "1px" }
    : undefined;

  const textareaProps =
    type === "textarea"
      ? { type: "text", multiline: true, maxRows: 7, minRows: 4 }
      : {};

  const inputProps = {
    id,
    name,
    placeholder,
    required,
    type,
    fullWidth: true,
    ...textareaProps,
    sx: {
      bgcolor: inputBgColor,
      border: generateBorder(defaultBorder),
      borderRadius: generateRadius(inputRadius),
      color: inputTextColor,
      fontSize: inputTextFontSize,
      fontWeight: inputTextFontWeight,
      height: type !== "textarea" ? inputHeight : "auto",
      minHeight: 30,

      "&:focus": {
        bgcolor: inputFocusBgColor,
        border: generateBorder(inputFocusBorder),
      },

      "& input": { px: isMat ? 0 : 1.5, py: 0 },

      "& textarea": { px: isMat ? 0 : 1.5, py: 0, resize: "vertical" },
    },
  };

  let field = (
    <TextField
      variant={variant as any}
      label={label}
      InputLabelProps={{ shrink: shrinkLabel }}
      {...inputProps}
    />
  );

  if (!isMat) {
    field = <InputBase {...inputProps} />;
  }

  const inputLabel =
    inputDesign !== "no-label" && !isMat ? (
      <InputLabel
        htmlFor={id}
        sx={{
          color: labelColor,
          fontSize: labelFontSize,
          fontWeight: labelFontWeight,
          width: inputDesign === "inline" ? labelWidth ?? 150 : "auto",
          textAlign: labelTextAlign,
        }}
      >
        {label ?? name}
      </InputLabel>
    ) : null;

  if (inputDesign === "block") {
    return (
      <Stack spacing={0.5}>
        {inputLabel}
        {field}
      </Stack>
    );
  }

  if (inputDesign === "inline") {
    return (
      <Stack spacing={0.5} direction="row" alignItems={labelVerticalAlign}>
        {inputLabel}
        {field}
      </Stack>
    );
  }

  return field;
};
